import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'

const teal = '#0D9488'
const plum = '#1E1040'
const cream = '#FAF7F2'

export default function Terms() {
  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <Link to="/" style={styles.back}>← Back</Link>
        <h1 style={styles.h1}>Terms of Service</h1>
        <p style={styles.effective}>Last updated: April 2026</p>

        <p style={styles.lead}>
          By creating an account and using Merq Log, you agree to these Terms of Service.
          Please read them — they're written in plain English on purpose.
        </p>

        <h2 style={styles.h2}>Who We Are</h2>
        <p style={styles.p}>
          Merq Log is a receipt and mileage tracking application operated by Merq Software,
          a sole proprietorship based in Texas, United States. References to "we," "us,"
          or "our" refer to Merq Software. References to "you" refer to the person using
          the application.
        </p>

        <h2 style={styles.h2}>What Merq Log Is</h2>
        <p style={styles.p}>
          Merq Log is a receipt and mileage tracking tool for self-employed people, freelancers,
          and contractors. It helps you scan and store receipts, log mileage trips, and generate
          tax-ready reports based on information you enter or upload.
        </p>
        <p style={styles.p}>
          <strong>Not tax advice.</strong> Merq Log is not a tax advisor, accountant, or any kind
          of licensed financial professional. Nothing in the app — including Schedule C reports,
          mileage deduction calculations, expense categorization, or any other feature —
          constitutes tax or financial advice. The IRS Standard Mileage Rate and other figures
          we display reflect publicly available information at the time of entry; rates change.
          All reports are based solely on data you enter. Always consult a qualified tax
          professional before filing or making financial decisions.
        </p>

        <h2 style={styles.h2}>Your Account</h2>
        <p style={styles.p}>To use Merq Log you must create an account with a valid email address. You are responsible for:</p>
        <ul style={styles.ul}>
          <li>Keeping your password secure and confidential</li>
          <li>All activity that occurs under your account</li>
          <li>Notifying us immediately at <a href="mailto:support@getmerq.com" style={styles.link}>support@getmerq.com</a> if you suspect unauthorized access</li>
        </ul>
        <p style={styles.p}>
          You must be at least 18 years old to create an account. By signing up, you confirm
          that you meet this requirement.
        </p>

        <h2 style={styles.h2}>Free Trial and Subscription</h2>
        <p style={styles.p}>
          Merq Log offers a 14-day free trial with no credit card required and no automatic charges.
          During the trial, you have full access to all features.
        </p>
        <p style={styles.p}>After your trial period:</p>
        <ul style={styles.ul}>
          <li>You may choose to upgrade to a paid plan to continue adding new receipts and trips</li>
          <li>Annual plan: <strong>$29/year</strong></li>
          <li>If you don't upgrade, your existing data remains viewable, but adding new items requires a subscription</li>
          <li>Auto-renewal is disclosed at checkout. We will never charge a renewal without sending you a reminder email first</li>
        </ul>

        <h2 style={styles.h2}>Receipt Scanning and AI</h2>
        <p style={styles.p}>
          When you scan a receipt, the image is sent to Anthropic's API to extract the merchant,
          amount, date, and other details. Per Anthropic's policy, the data is not used to train
          AI models. Extracted text is stored in your account so you can edit it later. The
          original image is stored securely in your account.
        </p>
        <p style={styles.p}>
          AI extraction is not perfect. Always review extracted details for accuracy before
          relying on them for tax or accounting purposes.
        </p>

        <h2 style={styles.h2}>Mileage and Location Data</h2>
        <p style={styles.p}>
          Merq Log uses Google Maps to autocomplete addresses and calculate distances between
          trip points. Address text and trip endpoints are sent to Google to provide this service.
          Merq Log does not track your real-time location.
        </p>

        <h2 style={styles.h2}>Your Data</h2>
        <p style={styles.p}>
          You own your data. We don't sell it, share it with advertisers, or use it for any purpose
          other than operating the service. See our <Link to="/privacy" style={styles.link}>Privacy Policy</Link> for full details.
        </p>
        <p style={styles.p}>
          You can export your data at any time from within the app. You may request full account
          deletion by contacting <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>.
        </p>

        <h2 style={styles.h2}>Acceptable Use</h2>
        <p style={styles.p}>You agree not to:</p>
        <ul style={styles.ul}>
          <li>Use Merq Log for any unlawful purpose</li>
          <li>Attempt to access another user's account or data</li>
          <li>Reverse engineer, copy, or resell any part of the application</li>
          <li>Upload receipts or trip data that does not belong to you</li>
          <li>Upload malicious files or content intended to harm the service or other users</li>
          <li>Use the app in any way that violates applicable laws in Texas or your jurisdiction</li>
        </ul>

        <h2 style={styles.h2}>Disclaimers</h2>
        <p style={styles.p}>
          Merq Log is provided "as is" without warranties of any kind. We do our best to keep the
          app running reliably, but we cannot guarantee:
        </p>
        <ul style={styles.ul}>
          <li>Uninterrupted or error-free service</li>
          <li>That data entered or extracted will be perfectly accurate or complete</li>
          <li>That the app will meet every tax or accounting need or situation</li>
          <li>That receipts or trips you enter will qualify for any specific deduction</li>
        </ul>
        <p style={styles.p}>
          We are not liable for any tax or financial decisions made based on information displayed in the app.
        </p>

        <h2 style={styles.h2}>Limitation of Liability</h2>
        <p style={styles.p}>
          To the fullest extent permitted by law, Merq Software's total liability to you for any
          claim arising from use of Merq Log shall not exceed the amount you paid for the service
          in the 12 months preceding the claim. We are not liable for indirect, incidental, or
          consequential damages of any kind.
        </p>

        <h2 style={styles.h2}>Refunds</h2>
        <p style={styles.p}>
          We offer a 14-day money-back guarantee on annual subscriptions. See our{' '}
          <Link to="/refunds" style={styles.link}>Refund Policy</Link> for details.
        </p>

        <h2 style={styles.h2}>Governing Law</h2>
        <p style={styles.p}>
          These Terms are governed by the laws of the State of Texas, United States, without regard
          to its conflict of law provisions. Any disputes shall be resolved in the courts of Texas.
        </p>

        <h2 style={styles.h2}>Changes to These Terms</h2>
        <p style={styles.p}>
          We may update these Terms from time to time. When we do, we will update the
          "Last updated" date at the top of this page. Continued use of the app after changes
          constitutes acceptance of the updated Terms.
        </p>

        <h2 style={styles.h2}>Contact Us</h2>
        <p style={styles.p}>
          Questions about these Terms? Reach us at <a href="mailto:legal@getmerq.com" style={styles.link}>legal@getmerq.com</a>.
        </p>
        <div style={styles.footer}>
          <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
          <Link to="/refunds" style={styles.footerLink}>Refund Policy</Link>
          <Link to="/contact" style={styles.footerLink}>Contact</Link>
        </div>
      </div>
    </div>
  )
}

const styles = {
  page: { minHeight: '100vh', background: cream, fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum, padding: '40px 20px 80px' },
  container: { maxWidth: '760px', margin: '0 auto' },
  back: { color: teal, textDecoration: 'none', fontSize: '14px', fontWeight: 600 },
  h1: { fontFamily: "'Fraunces', serif", fontSize: '40px', fontWeight: 900, letterSpacing: '-1px', margin: '20px 0 8px 0' },
  effective: { fontSize: '13px', color: '#6B7280', fontWeight: 500, marginBottom: '32px' },
  lead: { fontSize: '16px', lineHeight: 1.7, marginBottom: '32px', color: '#374151' },
  h2: { fontFamily: "'Fraunces', serif", fontSize: '24px', fontWeight: 800, letterSpacing: '-0.4px', marginTop: '40px', marginBottom: '14px' },
  p: { fontSize: '15px', lineHeight: 1.65, color: '#374151', marginBottom: '14px' },
  ul: { fontSize: '15px', lineHeight: 1.7, color: '#374151', paddingLeft: '20px', marginBottom: '14px' },
  link: { color: teal, fontWeight: 600 },
  address: { fontSize: '14px', lineHeight: 1.7, color: '#374151', background: '#fff', padding: '14px 18px', borderRadius: '10px', border: '1px solid #E5E7EB' },
  footer: { marginTop: '60px', paddingTop: '24px', borderTop: '1px solid #E5E7EB', display: 'flex', gap: '20px', flexWrap: 'wrap' },
  footerLink: { color: teal, textDecoration: 'none', fontSize: '13px', fontWeight: 600 },
}
